import { useRef } from 'react';
import { isCsvFile } from '@/utils/csvAttachment';
import type { BulkImportRightPanelMode } from '@/types/bulkUserImport';
import type { BulkUserRow } from '@/types/bulkUserImport';
import { BulkImportEmptyUpload } from './BulkImportEmptyUpload';
import { BulkImportEmptyUploadSkeleton } from './BulkImportEmptyUploadSkeleton';
import { BulkImportTableSkeleton } from './BulkImportTableSkeleton';
import { BulkImportUsersTable, type BulkImportTableMode } from './BulkImportUsersTable';
import type { BulkCredentialIssuePhase } from './BulkCredentialIssueFollowUp';

interface BulkImportRightPanelProps {
  mode: BulkImportRightPanelMode;
  rows: BulkUserRow[];
  fileName: string | null;
  systemLabel: string;
  onUploadFile: (file: File) => void;
  onUploadError: (message: string) => void;
  onRowUpdate?: (rowId: string, patch: Partial<BulkUserRow>) => void;
  onApprove?: () => void;
  approveDisabled?: boolean;
  showApprove?: boolean;
  readOnly?: boolean;
  tableMode?: BulkImportTableMode;
  credentialIssuePhase?: BulkCredentialIssuePhase;
  credentialToastCount?: number | null;
  onDismissCredentialToast?: () => void;
  onIssueAllMobile?: () => void;
  onIssueMobile?: (row: BulkUserRow) => void;
  onClosePanel?: () => void;
}

export function BulkImportRightPanel({
  mode,
  rows,
  fileName,
  systemLabel,
  onUploadFile,
  onUploadError,
  onRowUpdate,
  onApprove,
  approveDisabled,
  showApprove,
  readOnly = false,
  tableMode = 'review',
  credentialIssuePhase = 'idle',
  credentialToastCount = null,
  onDismissCredentialToast,
  onIssueAllMobile,
  onIssueMobile,
  onClosePanel,
}: BulkImportRightPanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const credentialIssueLoading =
    credentialIssuePhase === 'preparing' || credentialIssuePhase === 'issuing';

  if (mode === 'hidden') return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isCsvFile(file)) {
      onUploadError('Only .csv files are supported.');
      return;
    }
    onUploadFile(file);
  };

  return (
    <aside className="bulk-import__right-panel" aria-label="User import preview">
      {onClosePanel ? (
        <button
          type="button"
          className="bulk-import__panel-close"
          aria-label="Close panel"
          onClick={onClosePanel}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path
              d="M2 2l10 10M12 2L2 12"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          </svg>
        </button>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="bulk-import__empty-input"
        onChange={handleFileChange}
      />
      {mode === 'skeleton' ? <BulkImportTableSkeleton /> : null}
      {mode === 'upload-skeleton' ? <BulkImportEmptyUploadSkeleton /> : null}
      {mode === 'empty-upload' ? (
        <BulkImportEmptyUpload
          systemLabel={systemLabel}
          onFileSelect={onUploadFile}
          onError={onUploadError}
        />
      ) : null}
      {mode === 'data' && credentialIssueLoading ? (
        <BulkImportTableSkeleton
          footerAction={{
            label: credentialIssuePhase === 'preparing' ? 'Preparing credentials' : 'Issuing credentials',
            loading: true,
          }}
        />
      ) : null}
      {mode === 'data' && !credentialIssueLoading ? (
        <BulkImportUsersTable
          rows={rows}
          fileName={fileName}
          systemLabel={systemLabel}
          mode={tableMode}
          readOnly={readOnly}
          credentialIssuePhase={credentialIssuePhase}
          credentialToastCount={credentialToastCount}
          onDismissCredentialToast={onDismissCredentialToast}
          onIssueAllMobile={onIssueAllMobile}
          onIssueMobile={onIssueMobile}
          onUploadNewFile={readOnly ? undefined : () => fileInputRef.current?.click()}
          onRowUpdate={onRowUpdate}
          onApprove={onApprove}
          approveDisabled={approveDisabled}
          showApprove={showApprove}
        />
      ) : null}
    </aside>
  );
}
